import React, { useEffect, useRef } from 'react';

import type { SessionMessage } from '../../stores/sessionStore';
import { MessageBubble } from './MessageBubble';
import { TypingIndicator } from './TypingIndicator';

interface MessageListProps {
  messages: SessionMessage[];
  isStreaming: boolean;
  /** Current streaming text to display in the assistant bubble */
  streamingText: string;
  /** Agent colour hex for typing indicator */
  agentColour: string;
  /** Agent light bg class for message bubbles */
  agentBgClass: string;
  /** Agent name for aria labels */
  agentName: string;
  /** Session ID for saving insights */
  sessionId?: string | null;
}

/**
 * Scrollable message area displaying all chat messages and the typing indicator.
 * Auto-scrolls to the bottom on new messages.
 */
export function MessageList({
  messages,
  isStreaming,
  streamingText,
  agentColour,
  agentBgClass,
  agentName,
  sessionId,
}: MessageListProps): React.JSX.Element {
  const bottomRef = useRef<HTMLDivElement>(null);
  const isInitialLoad = useRef(true);

  // Auto-scroll to bottom only on new messages after initial load
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText, isStreaming]);

  return (
    <div
      className="flex-1 overflow-y-auto px-4 py-6 space-y-4"
      role="log"
      aria-live="polite"
      aria-label="Chat messages"
    >
      {messages.map((msg) => (
        <MessageBubble
          key={msg.id}
          role={msg.role}
          content={msg.content}
          agentBgClass={agentBgClass}
          agentName={agentName}
          sessionId={sessionId}
        />
      ))}

      {/* Streaming assistant message (in progress) */}
      {isStreaming && streamingText && (
        <MessageBubble
          role="assistant"
          content={streamingText}
          agentBgClass={agentBgClass}
          agentName={agentName}
          sessionId={sessionId}
        />
      )}

      {/* Typing indicator when streaming but no text yet */}
      {isStreaming && !streamingText && (
        <TypingIndicator colour={agentColour} />
      )}

      <div ref={bottomRef} />
    </div>
  );
}
